import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Filter, Loader2, ChevronRight, Download, Star, TrendingUp, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { HorizontalScroll, ScrollCard } from '../components/HorizontalScroll';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCheckoutSession } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Slug → DB category name mapping
const SLUG_MAP = {
  'credit-disputes': 'Credit Disputes & Credit Reporting',
  'business-credit': 'Business Credit & Business Setup',
  'court-motions': 'Court Motions & Legal Filings',
  'affidavits': 'Affidavits & Sworn Statements',
  'trust-estate': 'Trust & Estate Planning',
  'legal-notices': 'Legal Notices & Demand Letters',
  'landlord-tenant': 'Landlord-Tenant Disputes',
  'lawsuits': 'Lawsuits & Civil Complaints',
};

const DISPLAY_NAMES = {
  'credit-disputes': 'Credit Disputes',
  'business-credit': 'Business Credit',
  'court-motions': 'Court Motions',
  'affidavits': 'Affidavits',
  'trust-estate': 'Trust & Estate',
  'legal-notices': 'Legal Notices',
  'landlord-tenant': 'Landlord-Tenant',
  'lawsuits': 'Civil Lawsuit Templates & Filing Guide',
};

const BadgeTag = ({ badge }) => {
  if (!badge) return null;
  const config = {
    'Best Seller': { bg: 'bg-yellow-500', icon: <Star size={10} className="mr-1" /> },
    'Trending': { bg: 'bg-red-500', icon: <TrendingUp size={10} className="mr-1" /> },
    'Most Popular': { bg: 'bg-blue-500', icon: <Award size={10} className="mr-1" /> },
  };
  const c = config[badge] || { bg: 'bg-gray-500', icon: null };
  return (
    <span className={`${c.bg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center shadow-sm`}>
      {c.icon}{badge}
    </span>
  );
};

const CategoryPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState('all');
  const [purchasing, setPurchasing] = useState(null);

  const categoryName = SLUG_MAP[slug] || '';
  const displayName = DISPLAY_NAMES[slug] || slug;

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setSelectedSub('all');
      try {
        const resp = await axios.get(`${API}/products/category/${encodeURIComponent(categoryName)}`);
        setProducts(resp.data.products.filter(p => p.product_type === 'guide' || !p.product_type));
      } catch (err) {
        console.error('Error fetching category products:', err);
      } finally {
        setLoading(false);
      }
    };
    if (categoryName) fetchProducts();
  }, [categoryName]);

  const subcategories = [...new Set(products.map(p => p.subcategory))].sort();

  const filtered = selectedSub === 'all'
    ? products
    : products.filter(p => p.subcategory === selectedSub);

  const handleQuickBuy = async (productId) => {
    setPurchasing(productId);
    try {
      const session = await createCheckoutSession(productId);
      if (session.url) window.location.href = session.url;
    } catch { alert('Unable to process. Please try again.'); }
    finally { setPurchasing(null); }
  };

  if (!categoryName) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Category not found</h2>
          <Button onClick={() => window.location.href = '/shop'}>Browse All Guides</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Category Hero */}
      <section className="bg-gradient-to-r from-red-700 via-orange-600 to-orange-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-2">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <ChevronRight size={14} />
            <a href="/shop" className="hover:text-white transition-colors">Shop</a>
            <ChevronRight size={14} />
            <span className="text-white font-medium">{displayName}</span>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold" data-testid="category-title">{displayName}{displayName.toLowerCase().includes('guide') ? '' : ' Guides'}</h1>
          <p className="text-white/80 mt-1 text-sm md:text-base">{filtered.length} professional guides available</p>
        </div>
      </section>

      {/* Sub-category Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-3 overflow-x-auto no-scrollbar" data-testid="subcategory-filters">
            <Filter size={16} className="text-gray-400 shrink-0" />
            <button
              onClick={() => setSelectedSub('all')}
              className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                selectedSub === 'all'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              All ({products.length})
            </button>
            {subcategories.map(sub => {
              const count = products.filter(p => p.subcategory === sub).length;
              return (
                <button
                  key={sub}
                  onClick={() => setSelectedSub(sub)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedSub === sub
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${sub.replace(/\s+/g, '-')}`}
                >
                  {sub} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
          </div>
        ) : filtered.length > 0 ? (
          <HorizontalScroll>
            {filtered.map(product => (
              <ScrollCard key={product.id} width="w-56 sm:w-64">
                <div
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300 h-full"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="relative">
                    <BookMockup productId={product.id} title={product.title} size="card" badge={getBadge(product)} />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-1.5 line-clamp-2 text-center">{product.title}</h3>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <a
                        href={`/guide/${product.id}`}
                        className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors"
                        data-testid={`view-details-${product.id}`}
                      >
                        View Details
                      </a>
                      <button
                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                        onClick={() => window.location.href = `/guide/${product.id}`}
                        disabled={purchasing === product.id}
                        data-testid={`buy-btn-${product.id}`}
                      >
                        {purchasing === product.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              </ScrollCard>
            ))}
          </HorizontalScroll>
        ) : (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No guides found</h3>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CategoryPage;
